import React, { useState, useRef } from 'react';
import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { Upload, X, Check, RotateCw, RotateCcw, FlipHorizontal, FlipVertical } from 'lucide-react';

const ImageUploader = ({ 
  onImageCropped, 
  onImageRemoved,
  initialImage = null, 
  aspectRatio = 1, 
  circularCrop = false 
}) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [preview, setPreview] = useState(initialImage);
  const [isEditing, setIsEditing] = useState(false);
  const cropperRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageSrc(reader.result);
        setIsEditing(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleConfirmCrop = () => {
    if (typeof cropperRef.current?.cropper !== "undefined") {
      const cropper = cropperRef.current.cropper;
      
      // Get the cropped canvas
      const canvas = cropper.getCroppedCanvas({
        fillColor: '#fff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (!canvas) {
        alert('Failed to crop image. Please try again.');
        return;
      }

      // If circular crop, we still generate a square canvas from Cropper.js
      // We will rely on CSS border-radius to show it as a circle in the preview.

      canvas.toBlob((blob) => {
        if (!blob) {
          alert('Canvas is empty.');
          return;
        }
        
        const file = new File([blob], 'cropped_image.jpg', { type: 'image/jpeg' });
        const previewUrl = URL.createObjectURL(blob);
        
        setPreview(previewUrl);
        setIsEditing(false);
        setImageSrc(null); // Clear raw image source
        
        if (onImageCropped) {
          onImageCropped(file, previewUrl);
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const handleCancelCrop = () => {
    setIsEditing(false);
    setImageSrc(null);
  };

  const removeImage = () => {
    setPreview(null);
    if (onImageRemoved) {
      onImageRemoved();
    }
  };

  const rotate = (degree) => {
    if (cropperRef.current?.cropper) {
      cropperRef.current.cropper.rotate(degree);
    }
  };

  const flipX = () => {
    if (cropperRef.current?.cropper) {
      const cropper = cropperRef.current.cropper;
      cropper.scaleX(cropper.getData().scaleX === -1 ? 1 : -1);
    }
  };

  const flipY = () => {
    if (cropperRef.current?.cropper) {
      const cropper = cropperRef.current.cropper;
      cropper.scaleY(cropper.getData().scaleY === -1 ? 1 : -1);
    }
  };

  return (
    <div className="image-uploader-wrapper">
      {!preview && !isEditing && (
        <div style={{ 
            border: '2px dashed var(--border-color)', 
            borderRadius: '12px', 
            padding: '20px', 
            textAlign: 'center',
            background: '#F8F9FB',
            cursor: 'pointer',
            position: 'relative',
            marginBottom: '24px'
        }}>
          <input 
            type="file" 
            accept="image/*"
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 10 }}
            onChange={handleFileChange}
          />
          <Upload size={24} color="var(--text-secondary)" style={{ margin: '0 auto' }} />
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '8px' }}>
            Click to upload and edit image
          </p>
        </div>
      )}

      {preview && !isEditing && (
        <div style={{ position: 'relative', width: '100%', aspectRatio: aspectRatio.toString(), marginBottom: '24px', maxWidth: '200px' }}>
          <img 
            src={preview} 
            alt="Preview" 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover', 
              borderRadius: circularCrop ? '50%' : '12px', 
              border: '1px solid var(--border-color)' 
            }} 
          />
          <button 
            type="button"
            onClick={removeImage}
            style={{
              position: 'absolute', top: '-8px', right: '-8px', background: '#FF4D4D', color: 'white',
              border: 'none', borderRadius: '50%', width: '28px', height: '28px', display: 'flex',
              alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 20
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {isEditing && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.8)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: 'white', width: '90%', maxWidth: '800px', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '85vh' }}>
            
            {/* Header */}
            <div style={{ padding: '16px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>Advanced Image Editor</h3>
              <button type="button" onClick={handleCancelCrop} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-main)' }}>
                <X size={20} />
              </button>
            </div>

            {/* Cropper Area */}
            <div style={{ flex: 1, width: '100%', background: '#f5f5f5', overflow: 'hidden' }}>
              {/* We apply a class conditionally to make the crop box circular if needed */}
              <style>
                {`
                  ${circularCrop ? `
                  .cropper-view-box,
                  .cropper-face {
                    border-radius: 50%;
                  }
                  ` : ''}
                `}
              </style>
              <Cropper
                ref={cropperRef}
                src={imageSrc}
                style={{ height: '100%', width: '100%' }}
                // Cropper.js options
                aspectRatio={aspectRatio}
                guides={true}
                viewMode={1} // Restrict the crop box to not exceed the size of the canvas.
                dragMode="move"
                background={false}
                responsive={true}
                autoCropArea={0.8}
                checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
              />
            </div>

            {/* Controls Toolbar */}
            <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', borderTop: '1px solid #eee' }}>
              
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  type="button"
                  onClick={() => rotate(-90)}
                  title="Rotate Left"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: '#F8F9FB', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-main)' }}
                >
                  <RotateCcw size={18} />
                </button>
                <button 
                  type="button"
                  onClick={() => rotate(90)}
                  title="Rotate Right"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: '#F8F9FB', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-main)' }}
                >
                  <RotateCw size={18} />
                </button>
                <button 
                  type="button"
                  onClick={flipX}
                  title="Flip Horizontal"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: '#F8F9FB', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-main)', marginLeft: '8px' }}
                >
                  <FlipHorizontal size={18} />
                </button>
                <button 
                  type="button"
                  onClick={flipY}
                  title="Flip Vertical"
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', background: '#F8F9FB', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-main)' }}
                >
                  <FlipVertical size={18} />
                </button>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button 
                  type="button"
                  onClick={handleCancelCrop}
                  style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #ddd', background: 'white', cursor: 'pointer', fontWeight: '500' }}
                >
                  Cancel
                </button>
                <button 
                  type="button"
                  onClick={handleConfirmCrop}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px', borderRadius: '8px', border: 'none', background: 'var(--primary-orange)', color: 'white', cursor: 'pointer', fontWeight: '600' }}
                >
                  <Check size={18} /> Save Edits
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
